import { onCleanup } from 'solid-js';
import {
  customShadowlessElement,
  correctElementType,
  type CorrectComponentType,
} from '@brytdesigns/web-component-core/utils';

type Props = {
  sectionId: string;
  resultsSelector: string;
  filtersFormSelector: string;
  activeFiltersSelector: string;
};

const CollectionFilters: CorrectComponentType<Props> = (props, { element }) => {
  if (!props.sectionId) {
    console.warn('collection-filters: missing section-id attribute.');
    return;
  }

  const controller = new AbortController();
  const { signal } = controller;
  let inflight: AbortController | null = null;

  const getResults = () => element.querySelector<HTMLElement>(props.resultsSelector);
  const getActiveFilters = () => element.querySelector<HTMLElement>(props.activeFiltersSelector);

  const setLoading = (loading: boolean) => {
    if (loading) element.setAttribute('data-loading', '');
    else element.removeAttribute('data-loading');
    const results = getResults();
    if (results) results.setAttribute('aria-busy', String(loading));
  };

  const scrollSectionIntoView = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    element.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });
  };

  const render = async (url: string, pushState: boolean, scrollToTop = false) => {
    if (inflight) inflight.abort();
    inflight = new AbortController();
    setLoading(true);

    const sectionUrl = new URL(url, window.location.origin);
    sectionUrl.searchParams.set('section_id', props.sectionId);

    try {
      const response = await fetch(sectionUrl.toString(), { signal: inflight.signal });
      if (!response.ok) throw new Error(`collection-filters: fetch failed ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const incomingResults = doc.querySelector(props.resultsSelector);
      const currentResults = getResults();
      if (!incomingResults || !currentResults) {
        throw new Error('collection-filters: results container not found.');
      }
      currentResults.replaceWith(incomingResults);

      updateFilterCounts(doc);
      updateFilteredTotal(doc);
      updateActiveFilters(doc);

      if (pushState) {
        window.history.pushState({}, '', new URL(url, window.location.origin).toString());
      }

      syncFilterFormsFromUrl(url);

      if (scrollToTop) scrollSectionIntoView();

      element.dispatchEvent(new CustomEvent('collection-filters:rendered', { bubbles: true }));
    } catch (err) {
      if ((err as Error).name !== 'AbortError') console.error(err);
    } finally {
      setLoading(false);
      inflight = null;
    }
  };

  const updateFilterCounts = (doc: Document) => {
    const incomingLabels = doc.querySelectorAll<HTMLElement>('[data-filter-value-key]');
    const incomingByKey = new Map<string, HTMLElement>();
    incomingLabels.forEach((label) => {
      const key = label.getAttribute('data-filter-value-key');
      if (key) incomingByKey.set(key, label);
    });

    element.querySelectorAll<HTMLElement>('[data-filter-value-key]').forEach((label) => {
      const key = label.getAttribute('data-filter-value-key');
      if (!key) return;
      const incoming = incomingByKey.get(key);

      const countEl = label.querySelector<HTMLElement>('[data-filter-count]');
      const incomingCount = incoming?.querySelector<HTMLElement>('[data-filter-count]');
      if (countEl && incomingCount) countEl.textContent = incomingCount.textContent;
    });
  };

  const updateFilteredTotal = (doc: Document) => {
    const incoming = doc.querySelector<HTMLElement>('[data-filtered-total]');
    if (!incoming) return;
    element.querySelectorAll<HTMLElement>('[data-filtered-total]').forEach((el) => {
      el.textContent = incoming.textContent;
    });
  };

  const updateActiveFilters = (doc: Document) => {
    const incoming = doc.querySelector<HTMLElement>(props.activeFiltersSelector);
    const current = getActiveFilters();
    if (!incoming || !current) return;
    current.replaceChildren(...Array.from(incoming.children));
  };

  const syncFilterFormsFromUrl = (url: string) => {
    const params = new URL(url, window.location.origin).searchParams;

    element
      .querySelectorAll<HTMLInputElement>(`${props.filtersFormSelector} input[name^="filter."]`)
      .forEach((input) => {
        const values = params.getAll(input.name);

        if (input.type === 'checkbox' || input.type === 'radio') {
          input.checked = values.includes(input.value);
          return;
        }

        input.value = params.get(input.name) ?? '';
      });
  };

  const buildUrlFromForm = (form: HTMLFormElement): string => {
    const data = new FormData(form);
    const params = new URLSearchParams();
    for (const [key, value] of data.entries()) {
      if (typeof value === 'string' && value !== '') params.append(key, value);
    }
    const base = form.getAttribute('action') || window.location.pathname;
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  };

  const findForm = (target: EventTarget | null): HTMLFormElement | null => {
    if (!(target instanceof HTMLElement)) return null;
    const form = target.closest<HTMLFormElement>(props.filtersFormSelector);
    return form && element.contains(form) ? form : null;
  };

  const onFormChange = (event: Event) => {
    const form = findForm(event.target);
    if (!form) return;
    render(buildUrlFromForm(form), true);
  };

  const onFormSubmit = (event: Event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    const form = findForm(event.target);
    if (!form) return;
    event.preventDefault();
    render(buildUrlFromForm(form), true);
  };

  const onClick = (event: MouseEvent) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const clearLink = target.closest<HTMLAnchorElement>('a[data-clear-filters][href]');
    if (clearLink) {
      event.preventDefault();
      void render(clearLink.href, true, false);
      return;
    }

    const link = target.closest<HTMLAnchorElement>('[data-ajax-nav] a[href]');
    if (!link) return;

    const results = getResults();
    if (results && results.contains(link)) {
      event.preventDefault();
      void render(link.href, true, true);
      return;
    }

    const activeFilters = getActiveFilters();
    if (activeFilters && activeFilters.contains(link)) {
      event.preventDefault();
      void render(link.href, true, false);
    }
  };

  const onPopState = () => {
    render(window.location.href, false);
  };

  element.addEventListener('change', onFormChange, { signal });
  element.addEventListener('submit', onFormSubmit, { signal });
  element.addEventListener('click', onClick, { signal });
  window.addEventListener('popstate', onPopState, { signal });

  onCleanup(() => {
    controller.abort();
    if (inflight) inflight.abort();
  });
};

customShadowlessElement(
  'collection-filters',
  {
    sectionId: '',
    resultsSelector: '[data-results]',
    filtersFormSelector: '[data-filters-form]',
    activeFiltersSelector: '[data-active-filters]',
  },
  correctElementType(CollectionFilters),
);
